/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Component, Fragment } from 'react'
import { sortBy } from 'lodash'

import { EuiFlexGrid, EuiFlexGroup, EuiFlexItem, EuiFormHelpText, EuiSpacer } from '@elastic/eui'

import type { DeploymentSearchResponse } from '@modules/cloud-api/v1/types'

import { getDisplayId, getDisplayName } from '@/lib/stackDeployments/selectors/display'
import { isEsStopped } from '@/lib/stackDeployments/selectors/configurationChanges'

import DeploymentCard from './DeploymentCard'
import CustomPagination from './CustomPagination'
import { renderMatchSummary } from './utils/renderMatchSummary'

import type { TableChangeState } from './types'

const deploymentCardWrapperStyles = css`
  min-width: 0;

  @media only screen and (min-width: 992px) {
    .euiFlexGrid & {
      flex-basis: calc(50% - 1.5rem);
    }
  }

  @media only screen and (min-width: 1200px) {
    .euiFlexGrid & {
      flex-basis: calc(33.3% - 1.5rem);
    }
  }
`

type Props = {
  deployments: DeploymentSearchResponse[]
  pageSize: number | null
  shownCount: number | null
  matchCount: number | null
  maintainOrder: boolean
  pageIndex?: number
  hasNextPage?: boolean
  onTableChange?: (state: TableChangeState) => void
  cursorHistory?: string[]
  isLoading?: boolean
}

export default class DeploymentsCardList extends Component<Props> {
  render() {
    return (
      <Fragment>
        {this.renderDeploymentGrid()}
        {this.renderPagination()}
      </Fragment>
    )
  }

  renderDeploymentGrid() {
    const { deployments, maintainOrder } = this.props
    // Sort deployments similar to how it was done in getRows
    const ordering: Array<(DeploymentSearchResponse) => any> = [
      (deployment: DeploymentSearchResponse) => isEsStopped({ deployment }),
    ]

    if (!maintainOrder) {
      ordering.push(
        (deployment: DeploymentSearchResponse) => getDisplayName({ deployment }),
        (deployment: DeploymentSearchResponse) => getDisplayId({ deployment }),
      )
    }

    const sortedDeployments = sortBy<DeploymentSearchResponse>(deployments, ordering)

    return (
      <EuiFlexGrid columns={3}>
        {sortedDeployments.map((deployment) => (
          <EuiFlexItem
            key={deployment.id}
            className='deploymentCard-wrapper'
            css={deploymentCardWrapperStyles}
          >
            <DeploymentCard deployment={deployment} />
          </EuiFlexItem>
        ))}
      </EuiFlexGrid>
    )
  }

  renderPagination() {
    const {
      shownCount,
      matchCount,
      pageIndex = 0,
      hasNextPage = false,
      onTableChange,
      deployments,
      cursorHistory = [],
      isLoading = false,
    } = this.props

    // Calculate total pages once
    const totalPages =
      matchCount && this.props.pageSize ? Math.ceil(matchCount / this.props.pageSize) : null

    // Use totalPages for reliable pagination logic
    const showPagination = totalPages !== null && totalPages > 1
    const showMatchSummary = shownCount !== null && matchCount !== null

    if (!showMatchSummary && !showPagination) {
      return null
    }

    return (
      <Fragment>
        <EuiSpacer size='m' />

        <EuiFlexGroup alignItems='center' justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            {showMatchSummary && (
              <EuiFormHelpText>
                {renderMatchSummary({
                  page: pageIndex,
                  currentPageSize: deployments.length,
                  shownCount,
                  matchCount,
                  pageSize: this.props.pageSize,
                  totalPages,
                })}
              </EuiFormHelpText>
            )}
          </EuiFlexItem>

          {showPagination && (
            <EuiFlexItem grow={false}>
              <CustomPagination
                pageCount={totalPages}
                activePage={pageIndex}
                hasNextPage={hasNextPage}
                cursorHistory={cursorHistory}
                isLoading={isLoading}
                onPageClick={(nextPage) => {
                  if (onTableChange) {
                    onTableChange({ page: { index: nextPage } })
                  }
                }}
                aria-label='Deployment pagination'
              />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </Fragment>
    )
  }
}
